// components/admin/StaffManager.js - Complete with In Progress & Order History
import React, { useState, useEffect } from 'react';
import { dbOnValue, dbPush, dbUpdate, dbRemove, dbGet } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { 
  Users, Plus, Search, Edit2, Trash2, Eye, Clock, 
  CheckCircle, XCircle, Mail, Phone, Calendar, 
  Package, ShoppingBag, UserIcon, Loader, X,
  UserPlus, Shield, Key, RefreshCw,
  Truck, MapPin, TrendingUp,
  ClipboardList, PlayCircle, CheckSquare, Eye as EyeIcon,
  History, Activity, ArrowLeft
} from 'lucide-react';

const StaffManager = ({ staffList: propStaffList, onRefresh, orders }) => {
  const { user, isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedStaffOrders, setSelectedStaffOrders] = useState([]);
  const [staffAllocatedOrders, setStaffAllocatedOrders] = useState({});
  const [viewTab, setViewTab] = useState('inprogress'); // 'inprogress' or 'history'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    role: 'staff',
    status: 'active'
  });
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load staff from Firebase
  useEffect(() => {
    const unsubscribe = dbOnValue('users', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const staffData = Object.entries(data)
          .filter(([_, userData]) => userData.role === 'staff')
          .map(([id, userData]) => ({ 
            id: id,
            firebaseKey: id,
            staffId: id,
            ...userData,
            status: userData.status || 'active',
            createdAt: userData.createdAt || new Date().toISOString()
          }));
        setStaff(staffData);
        setLoading(false);
      } else {
        setStaff([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Calculate allocated orders and progress for each staff member
  useEffect(() => {
    if (!orders || !staff.length) return;
    
    const allocations = {};
    
    staff.forEach(staffMember => {
      const staffOrders = orders.filter(order => {
        const matchById = order.assignedToId === staffMember.id || 
                          order.assignedToId === staffMember.firebaseKey ||
                          order.assignedToId === staffMember.staffId;
        
        const matchByName = order.assignedTo === staffMember.name ||
                            order.assignedTo === staffMember.name?.toLowerCase();
        
        const matchByEmail = order.assignedToEmail === staffMember.email;
        
        return matchById || matchByName || matchByEmail;
      });
      
      allocations[staffMember.id] = {
        total: staffOrders.length,
        confirmed: staffOrders.filter(o => o.orderStatus === 'confirmed').length,
        processing: staffOrders.filter(o => o.orderStatus === 'processing').length,
        out: staffOrders.filter(o => o.orderStatus === 'out').length,
        delivered: staffOrders.filter(o => o.orderStatus === 'delivered').length,
        cancelled: staffOrders.filter(o => o.orderStatus === 'cancelled').length,
        orders: staffOrders,
        inProgressOrders: staffOrders.filter(o => ['confirmed', 'processing', 'out'].includes(o.orderStatus)),
        completedOrders: staffOrders.filter(o => o.orderStatus === 'delivered')
      };
    });
    
    setStaffAllocatedOrders(allocations);
  }, [orders, staff]);

  const getStaffStats = (staffMember) => {
    const stats = staffAllocatedOrders[staffMember.id] || {
      total: 0, confirmed: 0, processing: 0, out: 0, delivered: 0, cancelled: 0, orders: [],
      inProgressOrders: [], completedOrders: []
    };
    
    const totalOrders = stats.total;
    const completedOrders = stats.delivered;
    const inProgressOrders = stats.confirmed + stats.processing + stats.out;
    const completionRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;
    
    return { 
      totalOrders, 
      completedOrders,
      confirmedOrders: stats.confirmed,
      processingOrders: stats.processing,
      outOrders: stats.out,
      cancelledOrders: stats.cancelled,
      inProgressOrders: stats.inProgressOrders,
      completedOrdersList: stats.completedOrders,
      inProgressCount: inProgressOrders,
      completionRate
    };
  };

  const handleViewOrders = (staffMember) => {
    setSelectedStaff(staffMember);
    const stats = getStaffStats(staffMember);
    setSelectedStaffOrders(stats.inProgressOrders);
    setViewTab('inprogress');
    setShowViewModal(true);
  };

// In StaffManager.js, update the handleAddStaff function:

const handleAddStaff = async () => {
  if (!formData.name || !formData.email || !formData.password) {
    toast.error('Please fill all required fields');
    return;
  }

  setSubmitting(true);
  try {
    const newStaff = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || '',
      role: 'staff',
      status: 'active',
      password: formData.password,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const result = await dbPush('users', newStaff);
    toast.success(`Staff member added! Login credentials: Email: ${formData.email}, Password: ${formData.password}`);
    setShowAddModal(false);
    setFormData({ name: '', email: '', phone: '', password: '', newPassword: '', confirmPassword: '', role: 'staff', status: 'active' });
    if (onRefresh) onRefresh();
  } catch (error) {
    toast.error('Failed to add staff: ' + error.message);
  } finally {
    setSubmitting(false);
  }
};
  // Edit staff
  const handleUpdateStaff = async () => {
    if (!selectedStaff || !formData.name || !formData.email) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        status: formData.status,
        updatedAt: new Date().toISOString()
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await dbUpdate(`users/${selectedStaff.firebaseKey}`, updateData);
      toast.success('Staff member updated successfully!');
      setShowEditModal(false);
      setSelectedStaff(null);
      setFormData({ name: '', email: '', phone: '', password: '', newPassword: '', confirmPassword: '', role: 'staff', status: 'active' });
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update staff: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (staffMember) => {
    const newStatus = staffMember.status === 'active' ? 'inactive' : 'active';
    try {
      await dbUpdate(`users/${staffMember.firebaseKey}`, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      toast.success(`Staff ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditModal = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      name: staffMember.name || '',
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      password: '',
      newPassword: '',
      confirmPassword: '',
      role: 'staff',
      status: staffMember.status || 'active'
    });
    setShowPassword(false);
    setShowEditModal(true);
  };

  const filteredStaff = staff.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.phone?.includes(searchTerm)
  );

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return { color: '#10b981', bg: '#ecfdf5', label: 'Active', icon: CheckCircle };
    }
    return { color: '#ef4444', bg: '#fef2f2', label: 'Inactive', icon: XCircle };
  };

  // Switch between In Progress and History tabs in modal
  const switchViewTab = (tab) => {
    setViewTab(tab);
    const stats = getStaffStats(selectedStaff);
    if (tab === 'inprogress') {
      setSelectedStaffOrders(stats.inProgressOrders);
    } else {
      setSelectedStaffOrders(stats.completedOrdersList);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader size={40} className="spinner" style={{ color: '#dc2626' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a0a0a', marginBottom: '4px' }}>
            Staff Management
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
            Manage your team members and track their performance
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <UserPlus size={18} /> Add Staff Member
        </button>
      </div>

      {/* Stats Cards - Responsive Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>{staff.length}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Staff</div>
            </div>
          </div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={20} color="#10b981" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>
                {staff.filter(s => s.status === 'active').length}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Active Staff</div>
            </div>
          </div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardList size={20} color="#3b82f6" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>
                {Object.values(staffAllocatedOrders).reduce((sum, s) => sum + (s?.total || 0), 0)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Orders</div>
            </div>
          </div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckSquare size={20} color="#10b981" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>
                {Object.values(staffAllocatedOrders).reduce((sum, s) => sum + (s?.delivered || 0), 0)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Completed Orders</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: isMobile ? '12px' : '12px 16px', 
        marginBottom: '20px', 
        border: '1px solid #e2e8f0' 
      }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 40px 10px 40px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Staff Table - Responsive */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        border: '1px solid #e2e8f0', 
        overflow: 'hidden',
        overflowX: 'auto'
      }}>
        {filteredStaff.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
            <Users size={48} color="#cbd5e1" />
            <p style={{ marginTop: '12px' }}>No staff members found</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '700px' : '900px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                <th style={thStyle}>Staff</th>
                <th style={thStyle}>Contact</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Orders</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map(staffMember => {
                const stats = getStaffStats(staffMember);
                const statusConfig = getStatusBadge(staffMember.status);
                const StatusIcon = statusConfig.icon;
                const allocatedCount = staffAllocatedOrders[staffMember.id]?.total || 0;
                const completedCount = stats.completedOrders;
                const inProgressCount = stats.inProgressCount;
                
                return (
                  <tr key={staffMember.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: isMobile ? '32px' : '40px',
                          height: isMobile ? '32px' : '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: isMobile ? '0.9rem' : '1rem'
                        }}>
                          {staffMember.name?.[0]?.toUpperCase() || 'S'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a0a0a', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>{staffMember.name}</div>
                          <div style={{ fontSize: '0.6rem', color: '#64748b' }}>
                            ID: {staffMember.firebaseKey?.slice(0, 6)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Mail size={12} color="#94a3b8" /> {staffMember.email}
                        </span>
                        {staffMember.phone && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Phone size={12} color="#94a3b8" /> {staffMember.phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        borderRadius: '20px',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        background: statusConfig.bg,
                        color: statusConfig.color
                      }}>
                        <StatusIcon size={12} /> {statusConfig.label}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.1rem', color: '#2563eb' }}>
                          {allocatedCount}
                        </div>
                        
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ minWidth: isMobile ? '120px' : '160px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: '4px' }}>
                          <span>Completion</span>
                          <span style={{ fontWeight: 700, color: '#10b981' }}>{Math.round(stats.completionRate)}%</span>
                        </div>
                        <div style={{ width: '100%', height: '5px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${stats.completionRate}%`,
                            height: '100%',
                            background: '#10b981',
                            borderRadius: '3px'
                          }} />
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleViewOrders(staffMember)}
                          style={{
                            padding: isMobile ? '4px 8px' : '6px 12px',
                            background: '#e3f2fd',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: isMobile ? '0.65rem' : '0.7rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#1565c0',
                            fontWeight: 500
                          }}
                        >
                          <EyeIcon size={12} /> {!isMobile && 'View'}
                        </button>
                        <button
                          onClick={() => openEditModal(staffMember)}
                          style={{
                            padding: isMobile ? '4px 8px' : '6px 12px',
                            background: '#f1f5f9',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: isMobile ? '0.65rem' : '0.7rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#dc2626',
                            fontWeight: 500
                          }}
                        >
                          <Edit2 size={12} /> {!isMobile && 'Edit'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* View Orders Modal - Responsive with Tabs */}
      {showViewModal && selectedStaff && (
        <>
          <div onClick={() => setShowViewModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(4px)' }} />
          <div style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            background: 'white', 
            borderRadius: '16px', 
            zIndex: 1001, 
            width: isMobile ? '95%' : '90%',
            maxWidth: '900px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{ 
              padding: isMobile ? '12px 16px' : '16px 20px', 
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Package size={18} /> {selectedStaff.name}'s Orders
                  </h3>
                  <p style={{ fontSize: '0.65rem', opacity: 0.9, marginTop: '4px' }}>
                    Total: {staffAllocatedOrders[selectedStaff.id]?.total || 0} orders
                  </p>
                </div>
                <button onClick={() => setShowViewModal(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', color: 'white' }}>✕</button>
              </div>
              
              {/* Tabs */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                  onClick={() => switchViewTab('inprogress')}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    background: viewTab === 'inprogress' ? 'white' : 'rgba(255,255,255,0.2)',
                    color: viewTab === 'inprogress' ? '#dc2626' : 'white',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Activity size={14} /> In Progress ({selectedStaffOrders.filter(o => ['confirmed', 'processing', 'out'].includes(o.orderStatus)).length})
                </button>
                <button
                  onClick={() => switchViewTab('history')}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    background: viewTab === 'history' ? 'white' : 'rgba(255,255,255,0.2)',
                    color: viewTab === 'history' ? '#dc2626' : 'white',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <History size={14} /> History ({selectedStaffOrders.filter(o => o.orderStatus === 'delivered' || o.orderStatus === 'cancelled').length})
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '20px' }}>
              {selectedStaffOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Package size={48} color="#cbd5e1" />
                  <p style={{ marginTop: '16px', color: '#64748b', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    {viewTab === 'inprogress' ? 'No in-progress orders' : 'No order history'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedStaffOrders.map(order => {
                    const statusColors = {
                      pending: { bg: '#fef3c7', color: '#d97706', label: 'Pending' },
                      confirmed: { bg: '#dbeafe', color: '#2563eb', label: 'Confirmed' },
                      processing: { bg: '#fef3c7', color: '#d97706', label: 'Processing' },
                      out: { bg: '#ede9fe', color: '#7c3aed', label: 'Out for Delivery' },
                      delivered: { bg: '#d1fae5', color: '#059669', label: 'Delivered' },
                      cancelled: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled' }
                    };
                    const status = statusColors[order.orderStatus] || statusColors.pending;
                    
                    return (
                      <div key={order.id} style={{
                        background: '#f8fafc',
                        borderRadius: '10px',
                        padding: isMobile ? '10px' : '12px',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease'
                      }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.85rem', fontFamily: 'monospace' }}>#{order.id?.slice(0, 12)}</span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              background: status.bg,
                              color: status.color
                            }}>
                              {status.label}
                            </span>
                          </div>
                          <span style={{ fontWeight: 700, color: '#dc2626', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>₹{order.totalAmount?.toLocaleString()}</span>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '4px' }}>
                              <Calendar size={10} style={{ display: 'inline', marginRight: '4px' }} />
                              {new Date(order.orderDate).toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#1a0a0a' }}>
                              <UserIcon size={10} style={{ display: 'inline', marginRight: '4px' }} />
                              {order.customerName}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                              <Phone size={10} style={{ display: 'inline', marginRight: '4px' }} />
                              {order.customerPhone}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                              <MapPin size={10} style={{ display: 'inline', marginRight: '4px' }} />
                              {order.deliveryAddress?.line1}, {order.deliveryAddress?.city}
                            </div>
                            {order.deliveryTime && (
                              <div style={{ fontSize: '0.65rem', color: '#f59e0b', marginTop: '4px' }}>
                                <Clock size={10} style={{ display: 'inline', marginRight: '4px' }} />
                                Delivery: {order.deliveryTime}
                              </div>
                            )}
                            <div style={{ fontSize: '0.6rem', color: '#64748b', marginTop: '4px' }}>
                              Payment: {order.paymentMethod === 'Cash on Delivery' ? 'COD' : 'Online'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Items Preview */}
                        {order.items && order.items.length > 0 && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '0.6rem', color: '#64748b', marginBottom: '6px' }}>Items:</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {order.items.slice(0, isMobile ? 2 : 3).map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                  <img src={item.image} alt={item.name} style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                                  <span style={{ fontSize: '0.6rem' }}>{item.name} x{item.quantity}</span>
                                </div>
                              ))}
                              {order.items.length > (isMobile ? 2 : 3) && (
                                <span style={{ fontSize: '0.6rem', color: '#64748b', padding: '4px 8px' }}>
                                  +{order.items.length - (isMobile ? 2 : 3)} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Add Staff Modal - Responsive */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-container" style={{ maxWidth: '500px', width: isMobile ? '95%' : '90%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 700 }}>
                <UserPlus size={18} style={{ display: 'inline', marginRight: '8px', color: '#dc2626' }} />
                Add New Staff Member
              </h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter staff name"
                  style={inputModalStyle}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="staff@example.com"
                  style={inputModalStyle}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="10-digit number"
                  style={inputModalStyle}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Temporary Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  style={inputModalStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                <button onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', border: 'none', fontWeight: 500 }}>Cancel</button>
                <button onClick={handleAddStaff} disabled={submitting} style={{ flex: 1, padding: '10px', background: submitting ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', border: 'none', fontWeight: 600 }}>
                  {submitting ? 'Adding...' : 'Add Staff'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal - Responsive */}
      {showEditModal && selectedStaff && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-container" style={{ maxWidth: '500px', width: isMobile ? '95%' : '90%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 700 }}>
                <Edit2 size={18} style={{ display: 'inline', marginRight: '8px', color: '#dc2626' }} />
                Edit Staff Member
              </h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={inputModalStyle}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={inputModalStyle}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={inputModalStyle}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Leave blank to keep current"
                    style={inputModalStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {showPassword ? <EyeIcon size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={inputModalStyle}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                <button onClick={() => setShowEditModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', border: 'none', fontWeight: 500 }}>Cancel</button>
                <button onClick={handleUpdateStaff} disabled={submitting} style={{ flex: 1, padding: '10px', background: submitting ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', border: 'none', fontWeight: 600 }}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(2px);
        }
        .modal-container {
          background: white;
          border-radius: 16px;
          max-height: 85vh;
          overflow: auto;
        }
        .modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          background: white;
        }
        .modal-close {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #f1f5f9;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .modal-body {
          padding: 20px;
        }
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Responsive Table */
        @media (max-width: 768px) {
          th, td {
            padding: 10px 8px !important;
          }
        }
      `}</style>
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '14px 16px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tdStyle = {
  padding: '14px 16px',
  fontSize: '0.85rem',
  verticalAlign: 'middle'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#334155',
  marginBottom: '6px'
};

const inputModalStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none'
};

export default StaffManager;